import { useEffect, useState } from 'react';
import { ChevronUp, Loader2 } from 'lucide-react';
import { roadmapIcon } from '../lib/roadmapIcons';

type RoadmapItem = {
  id: string;
  title: string;
  description: string;
  icon: string;
  targetMonth: number | null;
  targetYear: number | null;
  status: 'planned' | 'in_progress' | 'shipped' | string;
  votes: number;
  votedByMe: boolean;
};

const MONTHS = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];

function targetLabel(m: number | null, y: number | null): string | null {
  if (m && y) return `${MONTHS[m - 1]!.toUpperCase()} ${y}`;
  if (y) return String(y);
  return null;
}

const STATUS_META: Record<string, { label: string; badge: string; iconBg: string; iconColor: string; border: string }> = {
  in_progress: {
    label: 'En desarrollo',
    badge: 'bg-accent-50 text-accent-700 border-accent-200',
    iconBg: 'bg-accent-50',
    iconColor: 'text-accent-600',
    border: 'border-accent-200 hover:border-accent-300',
  },
  planned: {
    label: 'Planificado',
    badge: 'bg-slate-100 text-slate-500 border-slate-200',
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-500',
    border: 'border-slate-200 hover:border-slate-300',
  },
  shipped: {
    label: 'Disponible',
    badge: 'bg-brand-50 text-brand-700 border-brand-200',
    iconBg: 'bg-brand-50',
    iconColor: 'text-brand-600',
    border: 'border-brand-100 hover:border-brand-300',
  },
};

export function RoadmapSection() {
  const [items, setItems] = useState<RoadmapItem[] | null>(null);
  const [voting, setVoting] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetch('/api/roadmap')
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        if (alive) setItems(d.items ?? []);
      })
      .catch(() => {
        if (alive) setItems([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function vote(item: RoadmapItem) {
    if (item.votedByMe || voting) return;
    setVoting(item.id);
    // Optimistic: bump the count + mark voted immediately.
    setItems((prev) =>
      prev?.map((i) => (i.id === item.id ? { ...i, votes: i.votes + 1, votedByMe: true } : i)) ?? prev,
    );
    try {
      const res = await fetch(`/api/roadmap/${item.id}/vote`, { method: 'POST' });
      if (res.ok) {
        const data = (await res.json()) as { votes: number };
        setItems((prev) =>
          prev?.map((i) => (i.id === item.id ? { ...i, votes: data.votes, votedByMe: true } : i)) ?? prev,
        );
      } else {
        // Roll back on failure (e.g. rate limited).
        setItems((prev) =>
          prev?.map((i) => (i.id === item.id ? { ...i, votes: Math.max(0, i.votes - 1), votedByMe: false } : i)) ?? prev,
        );
      }
    } catch {
      setItems((prev) =>
        prev?.map((i) => (i.id === item.id ? { ...i, votes: Math.max(0, i.votes - 1), votedByMe: false } : i)) ?? prev,
      );
    } finally {
      setVoting(null);
    }
  }

  // Hide the whole section while loading-empty so the landing doesn't flash an
  // empty grid; once loaded, if there are no items we render nothing.
  if (items === null) {
    return (
      <section id="roadmap" className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
        <div className="flex justify-center text-slate-300">
          <Loader2 size={28} className="animate-spin" />
        </div>
      </section>
    );
  }
  if (items.length === 0) return null;

  return (
    <section id="roadmap" className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
      <div className="text-center max-w-2xl mx-auto">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-600">Roadmap público</span>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900">Lo que viene a continuación.</h2>
        <p className="mt-3 text-slate-600">
          Construimos en abierto. Vota lo que más te interesa y nos dirá qué priorizar.
        </p>
      </div>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((r) => {
          const Icon = roadmapIcon(r.icon);
          const meta = STATUS_META[r.status] ?? STATUS_META.planned!;
          const when = targetLabel(r.targetMonth, r.targetYear);
          return (
            <div
              key={r.id}
              className={`rounded-2xl bg-white border p-6 flex flex-col gap-5 hover:shadow-lg transition-all duration-300 ${meta.border}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${meta.iconBg} ${meta.iconColor}`}>
                  <Icon size={19} />
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.12em] font-bold px-2.5 py-1 rounded-full border ${meta.badge}`}>
                    {meta.label}
                  </span>
                  {when && (
                    <span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-400">
                      {when}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 leading-snug">{r.title}</h3>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{r.description}</p>
              </div>

              {/* Vote control */}
              <button
                type="button"
                onClick={() => vote(r)}
                disabled={r.votedByMe || voting === r.id}
                className={`inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                  r.votedByMe
                    ? 'bg-brand-50 border-brand-200 text-brand-700 cursor-default'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-brand-300 hover:text-brand-700'
                }`}
                title={r.votedByMe ? 'Ya has votado esta feature' : 'Vota esta feature'}
              >
                {voting === r.id ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <ChevronUp size={15} className={r.votedByMe ? 'text-brand-600' : 'text-slate-400'} />
                )}
                <span className="tabular-nums">{r.votes}</span>
                <span className="text-xs font-medium opacity-80">
                  {r.votedByMe ? 'votado' : r.votes === 1 ? 'voto' : 'votos'}
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
